import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd, removeWillChangeOnAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLDivElement} menuDrawer - The slideable drawer panel containing the menu.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer'];

  /**
   * Pending close timer used to debounce the editor deselect → select sequence.
   * @type {number | null}
   */
  #designModeCloseTimer = null;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('focusin', this.#onFocusIn);
    this.#setupAnimatedElementListeners();

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', this.#onBlockSelect);
      document.addEventListener('shopify:block:deselect', this.#onBlockDeselect);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('focusin', this.#onFocusIn);
    document.removeEventListener('shopify:block:select', this.#onBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#onBlockDeselect);
    if (this.#designModeCloseTimer) clearTimeout(this.#designModeCloseTimer);
  }

  /**
   * Whether the editor event targets this drawer (the drawer block itself or one of its items).
   * @param {Event} event
   * @returns {boolean}
   */
  #isOwnBlock(event) {
    return event.target instanceof Element && (this.contains(event.target) || event.target.contains(this));
  }

  /**
   * Open the drawer (and the relevant submenu) when a block is selected in the theme editor.
   * @param {Event} event
   */
  #onBlockSelect = (event) => {
    if (!this.#isOwnBlock(event) || !(event.target instanceof Element)) return;

    if (this.#designModeCloseTimer) {
      clearTimeout(this.#designModeCloseTimer);
      this.#designModeCloseTimer = null;
    }

    // No native summary click in the editor, so open the <details> ourselves
    // before animating, otherwise the panel stays hidden by the UA stylesheet.
    this.refs.details.open = true;
    this.open();

    const submenu = event.target
      .closest('.menu-drawer__list-item')
      ?.querySelector(':scope > details.menu-drawer__menu-container');

    if (submenu instanceof HTMLDetailsElement) {
      submenu.open = true;
      const summary = submenu.querySelector('summary');
      if (summary) {
        const syntheticEvent = /** @type {Event} */ (/** @type {unknown} */ ({ target: summary }));
        this.open('submenu', syntheticEvent);
      }
    }
  };

  /**
   * Close the drawer when a block is deselected in the theme editor.
   * Deferred so the deselect → select sequence between two blocks doesn't flicker.
   * @param {Event} event
   */
  #onBlockDeselect = (event) => {
    if (!this.#isOwnBlock(event) || !(event.target instanceof Element)) return;

    // If a drawer item with an open submenu was deselected, close just that submenu
    // right away so switching between items reveals the newly selected one.
    const submenu = event.target
      .closest('.menu-drawer__list-item')
      ?.querySelector(':scope > details.menu-drawer__menu-container');

    if (submenu instanceof HTMLDetailsElement && submenu.classList.contains('menu-open')) {
      this.#close(submenu);
    }

    // Defer closing the whole drawer so the deselect → select sequence between two
    // blocks doesn't flicker it shut and back open.
    this.#designModeCloseTimer = setTimeout(() => {
      this.#designModeCloseTimer = null;
      this.close();
    }, 50);
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    if (this.#isSearchOpen()) {
      // Return focus to the first menu item
      const firstItem = /** @type {HTMLElement | null} */ (
        this.querySelector('.menu-drawer__navigation a[href], .menu-drawer__navigation summary')
      );
      firstItem?.focus();
      return;
    }

    const [openSubmenu] = this.#openSubmenus();
    this.#close(/** @type {HTMLDetailsElement} */ (openSubmenu) ?? this.refs.details);
  };

  /**
   * Collapse any open submenu when the drawer search input takes focus.
   * @param {FocusEvent} event
   */
  #onFocusIn = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('.search-inline')) return;

    this.#openSubmenus().forEach((submenu) => {
      this.#close(/** @type {HTMLDetailsElement} */ (submenu));
    });
  };

  /**
   * @returns {NodeListOf<HTMLElement>}
   */
  #openSubmenus = () => {
    return this.querySelectorAll('details.menu-drawer__menu-container.menu-open');
  };

  /**
   * @returns {boolean}
   */
  #isSearchOpen = () => {
    return !!this.querySelector('predictive-search-component:focus-within');
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   * @param {Event} event
   */
  toggle(event) {
    if (this.isOpen) {
      event?.preventDefault?.();
      return this.close();
    }
    return this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {string} [target]
   * @param {Event} [event]
   */
  open(target, event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    this.preventInitialAccordionAnimations(details);
    requestAnimationFrame(() => {
      details.classList.add('menu-open');

      if (target) {
        this.refs.menuDrawer.classList.add('menu-drawer--has-submenu-opened');
      }

      // Wait for the drawer animation to complete before trapping focus
      const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');
      // When a submenu is open, keep the drawer search bar (outside the submenu) in the focus trap.
      const trapOptions = target ? { include: '.search-inline' } : undefined;
      onAnimationEnd(drawer || details, () => trapFocus(details, trapOptions), { subtree: false });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    this.refs.menuDrawer.classList.remove('menu-drawer--has-submenu-opened');

    // Wait for the .menu-drawer element's transition, not the entire details subtree
    // This avoids waiting for child accordion/resource-card animations which can cause issues on Firefox
    const drawer = details.querySelector('.menu-drawer, .menu-drawer__submenu');

    onAnimationEnd(
      drawer || details,
      () => {
        reset(details);
        if (details === this.refs.details) {
          removeTrapFocus();
          const openDetails = this.querySelectorAll('details[open]:not(accordion-custom > details)');
          openDetails.forEach(reset);
        } else {
          trapFocus(this.refs.details);
        }
      },
      { subtree: false }
    );
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * Temporarily disables accordion animations to prevent unwanted transitions when the drawer opens.
   * Adds a no-animation class to accordion content elements, then removes it after 100ms to
   * re-enable animations for user interactions.
   * @param {HTMLDetailsElement} details - The details element containing the accordions
   */
  preventInitialAccordionAnimations(details) {
    const content = details.querySelectorAll('accordion-custom .details-content');

    content.forEach((element) => {
      if (element instanceof HTMLElement) {
        element.classList.add('details-content--no-animation');
      }
    });
    setTimeout(() => {
      content.forEach((element) => {
        if (element instanceof HTMLElement) {
          element.classList.remove('details-content--no-animation');
        }
      });
    }, 100);
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
